import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint';
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash';
import { faTv } from '@fortawesome/pro-solid-svg-icons/faTv';
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button } from 'ui-components/components/button';
import { FormattedMessage } from 'react-intl';
import { Checkbox } from 'ui-components/components/checkbox';

import { useDeprecatedLocalStorage, LS_HIDDEN_PRINTERS_KEY } from '../../../../hooks/use-local-storage';
import messages from './../messages';
import styles from './styles.module.less';

export const WorkstationGroupPropType = PropTypes.shape({
  printers: PropTypes.array.isRequired,
  workstationId: PropTypes.string.isRequired,
});

const Printer = ({ printer, setHiddenPrinters, hidden, setPrintSettingForDocType }) => {
  const { id, name } = printer;
  const pickPrinter = useCallback(() => {
    setPrintSettingForDocType({
      ...printer,
      isSsc: true, // There isn't actually much context in v2 about this but I'm assuming it's shipstation connect. We don't need it for v3 but v2... might.
      isGcp: false, // same here but google cloud print.
    });
  }, [setPrintSettingForDocType, printer]);

  const togglePrinterHiddenState = useCallback(
    (e) => {
      e.stopPropagation();
      setHiddenPrinters((hiddenPrinters) => ({
        ...hiddenPrinters,
        [id]: !hiddenPrinters[id],
      }));
    },
    [setHiddenPrinters, id],
  );
  const hideMessage = hidden ? messages.unhide : messages.hide;

  return (
    <div
      className={classnames(styles.printer, {
        [styles['hidden-printer']]: hidden,
      })}
    >
      {/** the button component was doing something with long overflowing printer names that caused undesirable styling. */}
      <span className={styles['printer-name']} onClick={pickPrinter}>
        <FontAwesomeIcon className={styles['ws-icon']} icon={faPrint} />
        {name}
      </span>
      <Button className={styles.disabler} onClick={togglePrinterHiddenState} theme='link'>
        <FontAwesomeIcon className={styles['ws-icon']} icon={faEyeSlash} />
        <FormattedMessage {...hideMessage} />
      </Button>
    </div>
  );
};

Printer.propTypes = {
  printer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    workstationId: PropTypes.string.isRequired,
  }).isRequired,
  setHiddenPrinters: PropTypes.func.isRequired,
  hidden: PropTypes.bool.isRequired,
  setPrintSettingForDocType: PropTypes.func.isRequired,
};

const WorkstationGroup = ({ group, name, setHiddenPrinters, hiddenPrinters, showHidden, setPrintSettingForDocType }) => {
  return (
    <div>
      <div className={styles['workstation-info']}>
        <span className={styles['workstation-name']}>
          <FontAwesomeIcon className={styles['ws-icon']} icon={faTv} />
          {name}
        </span>
        <span>
          <FormattedMessage {...messages.printers} values={{ printerCount: group.printers.length }} />
        </span>
      </div>
      <div className={styles['workstation-printers']}>
        {group.printers.reduce((printers, printer) => {
          const { id } = printer;
          const curPrinterIsHidden = !!hiddenPrinters[id];
          const Component = (
            <Printer
              key={id}
              hidden={curPrinterIsHidden}
              printer={printer}
              setHiddenPrinters={setHiddenPrinters}
              setPrintSettingForDocType={setPrintSettingForDocType}
            />
          );
          return curPrinterIsHidden && !showHidden ? printers : [...printers, Component];
        }, [])}
      </div>
    </div>
  );
};

WorkstationGroup.propTypes = {
  group: WorkstationGroupPropType.isRequired,
  name: PropTypes.string.isRequired,
  hiddenPrinters: PropTypes.object.isRequired,
  setHiddenPrinters: PropTypes.func.isRequired,
  showHidden: PropTypes.bool.isRequired,
  setPrintSettingForDocType: PropTypes.func.isRequired,
};

const Workstations = ({ printersByWorkstation, setPrintSettingForDocType }) => {
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenPrinters, setHiddenPrinters] = useDeprecatedLocalStorage(LS_HIDDEN_PRINTERS_KEY, {});

  return (
    <>
      <div className={styles['show-hidden-checkbox']}>
        <Checkbox checked={showHidden} label={<FormattedMessage {...messages.showHiddenPrinters} />} onChange={setShowHidden} />
      </div>
      <div>
        {printersByWorkstation.map((p) => (
          <WorkstationGroup
            key={p.workstationId}
            group={p}
            hiddenPrinters={hiddenPrinters}
            name={p.workstationName}
            setHiddenPrinters={setHiddenPrinters}
            setPrintSettingForDocType={setPrintSettingForDocType}
            showHidden={showHidden}
          />
        ))}
      </div>
    </>
  );
};

Workstations.propTypes = {
  printersByWorkstation: PropTypes.arrayOf(WorkstationGroupPropType).isRequired,
  setPrintSettingForDocType: PropTypes.func.isRequired,
};

export default Workstations;
